import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, PartyPopper, Globe, Loader2 } from "lucide-react";
import { Card, Btn, Field, inputCls, CheckboxGroup, RadioGroupPills } from "../../components/dash/Kit";
import { INDUSTRY_OPTIONS } from "../../data/site";
import WebsiteImport from "../../components/dash/WebsiteImport";
import { api, errMessage } from "../../lib/api";

const STEPS = [
  ["business", "Business"],
  ["audience", "Audience"],
  ["brand", "Brand"],
  ["visual", "Visual"],
  ["social", "Social Media"],
  ["content", "Content"],
  ["goals", "Goals"],
  ["promotions", "Promotions"],
  ["uploads", "Uploads"],
  ["permissions", "Permissions"],
  ["review", "Review"],
];

const AGE_GROUPS = ["Under 18", "18–24", "25–34", "35–44", "45–54", "55–64", "65+"];
const ACTIONS = ["Visit location", "Make reservation", "Book appointment", "Buy product", "Visit website", "Send message", "Call", "Follow social account", "Join mailing list", "Request quotation", "Other"];
const TONES = ["Professional", "Friendly", "Luxury", "Playful", "Relaxed", "Bold", "Modern", "Informative", "Funny", "Premium", "Minimal", "Energetic", "Warm"];
const VISUAL_STYLES = ["Minimal", "Luxury", "Bold", "Colorful", "Modern", "Classic", "Natural", "Elegant", "Playful", "Industrial", "Other"];
const PLATFORMS = ["Instagram", "Facebook", "TikTok", "LinkedIn", "Other"];
const CONTENT_TYPES = ["Product/service promotion", "Educational content", "Entertainment", "Behind the scenes", "Staff introductions", "Customer reviews", "Offers", "Events", "Seasonal content", "Tips", "FAQs", "Before/after", "Reels", "Trends", "Community content", "Brand storytelling"];
const CTA_OPTIONS = ["Book now", "Reserve now", "Visit us", "Shop now", "Learn more", "Send us a message", "Call us", "Visit website", "Custom"];
const GOALS = ["Increase followers", "Get more bookings", "Increase reservations", "Increase website visitors", "Sell more products", "Build brand awareness", "Promote new products", "Promote events", "Increase customer engagement", "Look more professional", "Stay consistent", "Generate leads"];
const UPLOAD_CATEGORIES = ["Logo", "Products", "Food", "Location", "Team", "Customers", "Services", "Before & After", "Videos", "Promotions", "Other"];

const emptyPromotion = {
  name: "", description: "", start_date: "", end_date: "", price: "", discount: "",
  promo_code: "", url: "", cta: "", notes: "",
};

export default function BrandProfile() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [completedSteps, setCompletedSteps] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [media, setMedia] = useState([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api
      .get("/brand-profile")
      .then((r) => {
        setData(r.data.steps || {});
        setCompletedSteps(r.data.completed_steps || []);
        setCompleted(!!r.data.completed);
      })
      .finally(() => setLoading(false));
    api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
  }, []);

  const key = STEPS[step][0];
  const s = useMemo(() => data[key] || {}, [data, key]);
  const setS = (patch) => setData((d) => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));
  const field = (name) => (e) => setS({ [name]: e.target.value });

  const validate = () => {
    const n = {};
    if (key === "business") {
      if (!s.public_name?.trim()) n.public_name = "Please enter your business name.";
      if (!s.industry) n.industry = "Please choose your industry.";
      if (!s.description?.trim()) n.description = "Please describe your business.";
    }
    if (key === "audience" && !s.ideal_customer?.trim()) n.ideal_customer = "Please describe your ideal customer.";
    if (key === "brand" && (s.tone_words || []).length === 0) n.tone_words = "Please select at least one option.";
    if (key === "social" && (s.platforms || []).length === 0) n.platforms = "Please select at least one platform.";
    if (key === "content" && (s.content_types || []).length === 0) n.content_types = "Please select at least one content type.";
    if (key === "permissions") {
      if (s.can_use !== "Yes") n.can_use = "This permission is required to create content from your media.";
      if (s.owns_rights !== "Yes") n.owns_rights = "Please confirm you own or have permission to use your uploads.";
    }
    setErrors(n);
    return Object.keys(n).length === 0;
  };

  const persist = async (silent = false) => {
    setSaving(true);
    try {
      const { data: res } = await api.put("/brand-profile", { step: key, data: s });
      setCompletedSteps(res.completed_steps || []);
      if (!silent) toast.success("Progress saved.");
      return true;
    } catch (e) {
      toast.error(errMessage(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    if (!validate()) {
      toast.error("Please check the highlighted fields.");
      return;
    }
    if (await persist(true)) {
      setStep((v) => Math.min(v + 1, STEPS.length - 1));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const back = () => {
    setStep((v) => Math.max(v - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveExit = async () => {
    await persist();
    navigate("/dashboard");
  };

  const complete = async () => {
    if (!s.confirm_accurate) {
      setErrors({ confirm_accurate: "Please confirm the information is accurate." });
      return;
    }
    await persist(true);
    try {
      await api.post("/brand-profile/complete");
      setDone(true);
      setCompleted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const promotions = s.items || [];
  const setPromotions = (items) => setS({ items });

  if (loading) return <Card testId="brand-profile-loading">Loading your Brand Profile…</Card>;

  if (done) {
    return (
      <Card testId="brand-profile-complete" className="text-center">
        <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFF3EF] text-[#F95738]">
          <PartyPopper className="h-6 w-6" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-display text-2xl font-bold text-slate-900">Your Brand Profile is complete 🎉</h1>
        <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
          PostMint now has the information needed to start building content around your business.
        </p>
        <Link
          to="/dashboard"
          data-testid="go-to-dashboard-button"
          className="mt-6 inline-flex rounded-xl bg-[#F95738] px-5 py-3 text-sm font-semibold text-white"
        >
          Go to Dashboard
        </Link>
      </Card>
    );
  }

  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <div className="space-y-6" data-testid="brand-profile-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">Brand Profile</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
          This is what keeps your content recognisably yours. You can save and continue later at any time.
        </p>
      </div>

      <Card testId="brand-profile-progress">
        <div className="flex items-center justify-between text-sm">
          <p className="font-semibold text-slate-900">
            Step {step + 1} of {STEPS.length} · {STEPS[step][1]}
          </p>
          <p className="text-slate-500">{progress}%</p>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
          <div className="h-full rounded-full bg-[#F95738] transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {STEPS.map(([k, label], i) => (
            <button
              key={k}
              type="button"
              data-testid={`profile-step-tab-${k}`}
              onClick={() => setStep(i)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                i === step
                  ? "bg-slate-900 text-white"
                  : completedSteps.includes(k)
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-slate-100 text-slate-500"
              }`}
            >
              {i + 1} {label}
            </button>
          ))}
        </div>
      </Card>

      <Card testId={`profile-step-${key}`}>
        {/* STEP 1 BUSINESS */}
        {key === "business" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">Business information</h2>
            <WebsiteImport
              initialUrl={s.website || ""}
              onApplied={(profile) => {
                setData(profile.steps || {});
                setCompletedSteps(profile.completed_steps || []);
              }}
            />
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Legal Business Name" htmlFor="bp-legal">
                <input id="bp-legal" data-testid="bp-legal-name-input" className={inputCls} value={s.legal_name || ""} onChange={field("legal_name")} />
              </Field>
              <Field label="Public/Trading Business Name *" error={errors.public_name} htmlFor="bp-public">
                <input id="bp-public" data-testid="bp-public-name-input" className={inputCls} value={s.public_name || ""} onChange={field("public_name")} />
              </Field>
              <Field label="KvK Number" htmlFor="bp-kvk">
                <input id="bp-kvk" data-testid="bp-kvk-input" className={inputCls} value={s.kvk || ""} onChange={field("kvk")} />
              </Field>
              <Field label="VAT Number" htmlFor="bp-vat">
                <input id="bp-vat" data-testid="bp-vat-input" className={inputCls} value={s.vat || ""} onChange={field("vat")} />
              </Field>
              <Field label="Website" htmlFor="bp-website">
                <input id="bp-website" data-testid="bp-website-input" className={inputCls} value={s.website || ""} onChange={field("website")} />
              </Field>
              <Field label="Business Email" htmlFor="bp-email">
                <input id="bp-email" type="email" data-testid="bp-email-input" className={inputCls} value={s.email || ""} onChange={field("email")} />
              </Field>
              <Field label="Business Phone Number" htmlFor="bp-phone">
                <input id="bp-phone" data-testid="bp-phone-input" className={inputCls} value={s.phone || ""} onChange={field("phone")} />
              </Field>
              <Field label="Street" htmlFor="bp-street">
                <input id="bp-street" data-testid="bp-street-input" className={inputCls} value={s.street || ""} onChange={field("street")} />
              </Field>
              <Field label="House Number" htmlFor="bp-house">
                <input id="bp-house" data-testid="bp-house-input" className={inputCls} value={s.house_number || ""} onChange={field("house_number")} />
              </Field>
              <Field label="Postal Code" htmlFor="bp-postal">
                <input id="bp-postal" data-testid="bp-postal-input" className={inputCls} value={s.postal_code || ""} onChange={field("postal_code")} />
              </Field>
              <Field label="City" htmlFor="bp-city">
                <input id="bp-city" data-testid="bp-city-input" className={inputCls} value={s.city || ""} onChange={field("city")} />
              </Field>
              <Field label="Country" htmlFor="bp-country">
                <input id="bp-country" data-testid="bp-country-input" className={inputCls} value={s.country || "Netherlands"} onChange={field("country")} />
              </Field>
              <Field label="Industry *" error={errors.industry}>
                <select data-testid="bp-industry-select" className={inputCls} value={s.industry || ""} onChange={field("industry")}>
                  <option value="">Choose your industry</option>
                  {INDUSTRY_OPTIONS.map((i) => (<option key={i} value={i}>{i}</option>))}
                </select>
              </Field>
              {s.industry === "Other" && (
                <Field label="Describe your industry" htmlFor="bp-industry-other">
                  <input id="bp-industry-other" data-testid="bp-industry-other-input" className={inputCls} value={s.industry_other || ""} onChange={field("industry_other")} />
                </Field>
              )}
            </div>

            {[
              ["description", "Describe your business in your own words. *", errors.description],
              ["offerings", "What products or services do you offer?"],
              ["promote", "What are your main products/services you want promoted?"],
              ["differentiator", "What makes your business different from competitors?"],
            ].map(([name, label, error]) => (
              <Field key={name} label={label} error={error} htmlFor={`bp-${name}`}>
                <textarea id={`bp-${name}`} rows={4} data-testid={`bp-${name}-input`} className={inputCls} value={s[name] || ""} onChange={field(name)} />
              </Field>
            ))}

            <Field label="How long have you been operating?">
              <RadioGroupPills
                name="bp-operating"
                options={["Not launched yet", "Less than 1 year", "1–3 years", "3–5 years", "5+ years"]}
                value={s.operating || ""}
                onChange={(v) => setS({ operating: v })}
              />
            </Field>
          </div>
        )}

        {/* STEP 2 AUDIENCE */}
        {key === "audience" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">Target audience</h2>
            <Field label="Who is your ideal customer? *" error={errors.ideal_customer} htmlFor="bp-ideal">
              <textarea id="bp-ideal" rows={4} data-testid="bp-ideal-customer-input" className={inputCls} value={s.ideal_customer || ""} onChange={field("ideal_customer")} />
            </Field>
            <Field label="Age groups (choose all that apply)">
              <CheckboxGroup name="bp-age" columns={4} options={AGE_GROUPS} value={s.age_groups || []} onChange={(v) => setS({ age_groups: v })} />
            </Field>
            <Field label="Customer location">
              <RadioGroupPills name="bp-location" options={["Local", "Regional", "Netherlands", "International", "Custom"]} value={s.location_scope || ""} onChange={(v) => setS({ location_scope: v })} />
            </Field>
            {[
              ["customer_location", "Where are most customers located?"],
              ["problems_solved", "What problems does your business solve for customers?"],
              ["why_you", "Why do customers choose you?"],
            ].map(([name, label]) => (
              <Field key={name} label={label} htmlFor={`bp-${name}`}>
                <textarea id={`bp-${name}`} rows={3} data-testid={`bp-${name}-input`} className={inputCls} value={s[name] || ""} onChange={field(name)} />
              </Field>
            ))}
            <Field label="What actions would you most like customers to take?">
              <CheckboxGroup name="bp-actions" columns={3} options={ACTIONS} value={s.desired_actions || []} onChange={(v) => setS({ desired_actions: v })} />
            </Field>
          </div>
        )}

        {/* STEP 3 BRAND */}
        {key === "brand" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">Brand personality</h2>
            <Field label="How should your brand sound? *" error={errors.tone_words}>
              <CheckboxGroup name="bp-tone" columns={3} options={TONES} value={s.tone_words || []} onChange={(v) => setS({ tone_words: v })} />
            </Field>
            <Field label={`Preferred tone: ${["Very Casual", "Casual", "Balanced", "Formal", "Very Formal"][s.tone_level ?? 2]}`} htmlFor="bp-tone-slider">
              <input
                id="bp-tone-slider"
                type="range"
                min="0"
                max="4"
                step="1"
                data-testid="bp-tone-slider"
                className="w-full accent-[#F95738]"
                value={s.tone_level ?? 2}
                onChange={(e) => setS({ tone_level: Number(e.target.value) })}
              />
              <div className="mt-1 flex justify-between text-xs text-slate-400">
                <span>Very Casual</span><span>Balanced</span><span>Very Formal</span>
              </div>
            </Field>
            {[
              ["favourite_phrases", "Are there any words or phrases you frequently use?"],
              ["forbidden_words", "Are there any words or topics we should NEVER use?"],
            ].map(([name, label]) => (
              <Field key={name} label={label} htmlFor={`bp-${name}`}>
                <textarea id={`bp-${name}`} rows={3} data-testid={`bp-${name}-input`} className={inputCls} value={s[name] || ""} onChange={field(name)} />
              </Field>
            ))}
            <Field label="Do you use emojis?">
              <RadioGroupPills name="bp-emoji" options={["Yes, frequently", "Sometimes", "Rarely", "Never"]} value={s.emoji || ""} onChange={(v) => setS({ emoji: v })} />
            </Field>
            <Field label="Which languages should your content use?">
              <RadioGroupPills name="bp-language" options={["Dutch", "English", "Dutch + English", "Other"]} value={s.language || ""} onChange={(v) => setS({ language: v })} />
            </Field>
            <Field label='Should the content use "je/jij" or "u" in Dutch?'>
              <RadioGroupPills name="bp-formality" options={["Je/Jij", "U", "Depends on context"]} value={s.dutch_formality || ""} onChange={(v) => setS({ dutch_formality: v })} />
            </Field>
            <Field label="Describe your brand personality in 3–5 words." htmlFor="bp-personality">
              <input id="bp-personality" data-testid="bp-personality-input" className={inputCls} value={s.personality || ""} onChange={field("personality")} />
            </Field>
          </div>
        )}

        {/* STEP 4 VISUAL */}
        {key === "visual" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">Visual brand</h2>
            <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
              Upload your logo, secondary logo and brand guidelines (PDF, PNG, JPG, JPEG) on the{" "}
              <Link to="/dashboard/media" className="font-semibold text-[#F95738] hover:underline">Upload Media</Link>{" "}
              page using the <strong>Logo</strong> category. Files you have already uploaded: {media.length}.
            </p>
            <div className="grid gap-5 sm:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Field key={i} label={`Brand Color ${i}`} htmlFor={`bp-color-${i}`}>
                  <div className="flex items-center gap-3">
                    <input
                      id={`bp-color-${i}`}
                      type="color"
                      data-testid={`bp-color-${i}-input`}
                      className="h-11 w-14 cursor-pointer rounded-lg border border-slate-300"
                      value={s[`color_${i}`] || "#F95738"}
                      onChange={(e) => setS({ [`color_${i}`]: e.target.value })}
                    />
                    <input className={inputCls} value={s[`color_${i}`] || ""} onChange={(e) => setS({ [`color_${i}`]: e.target.value })} placeholder="#F95738" />
                  </div>
                </Field>
              ))}
            </div>
            <Field label="Do you know your brand fonts?">
              <RadioGroupPills name="bp-fonts-known" options={["Yes", "No"]} value={s.fonts_known || ""} onChange={(v) => setS({ fonts_known: v })} />
            </Field>
            {s.fonts_known === "Yes" && (
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Heading font" htmlFor="bp-heading-font">
                  <input id="bp-heading-font" data-testid="bp-heading-font-input" className={inputCls} value={s.heading_font || ""} onChange={field("heading_font")} />
                </Field>
                <Field label="Body font" htmlFor="bp-body-font">
                  <input id="bp-body-font" data-testid="bp-body-font-input" className={inputCls} value={s.body_font || ""} onChange={field("body_font")} />
                </Field>
              </div>
            )}
            <Field label="Describe the visual style you prefer.">
              <CheckboxGroup name="bp-visual-style" columns={3} options={VISUAL_STYLES} value={s.visual_styles || []} onChange={(v) => setS({ visual_styles: v })} />
            </Field>
            <Field label="Are there any colors or styles we should avoid?" htmlFor="bp-avoid-visual">
              <textarea id="bp-avoid-visual" rows={3} data-testid="bp-avoid-visual-input" className={inputCls} value={s.avoid_visual || ""} onChange={field("avoid_visual")} />
            </Field>
          </div>
        )}

        {/* STEP 5 SOCIAL */}
        {key === "social" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">Social media</h2>
            <div className="grid gap-5 sm:grid-cols-2">
              {[
                ["instagram", "Instagram URL / Username"],
                ["tiktok", "TikTok URL / Username"],
                ["facebook", "Facebook Page"],
                ["linkedin", "LinkedIn"],
                ["youtube", "YouTube"],
                ["pinterest", "Pinterest"],
                ["other_channel", "Other"],
              ].map(([name, label]) => (
                <Field key={name} label={label} htmlFor={`bp-${name}`}>
                  <input id={`bp-${name}`} data-testid={`bp-${name}-input`} className={inputCls} value={s[name] || ""} onChange={field(name)} />
                </Field>
              ))}
            </div>
            <Field label="Which platforms do you want content for? *" error={errors.platforms}>
              <CheckboxGroup name="bp-platforms" columns={3} options={PLATFORMS} value={s.platforms || []} onChange={(v) => setS({ platforms: v })} />
            </Field>
            <Field label="How often do you currently post?">
              <RadioGroupPills
                name="bp-frequency"
                options={["Never", "Less than once a month", "A few times a month", "1–2 times per week", "3–5 times per week", "Daily"]}
                value={s.frequency || ""}
                onChange={(v) => setS({ frequency: v })}
              />
            </Field>
            {[
              ["best_content", "What type of content currently performs best?"],
              ["worst_content", "Which posts have performed poorly?"],
            ].map(([name, label]) => (
              <Field key={name} label={label} htmlFor={`bp-${name}`}>
                <textarea id={`bp-${name}`} rows={3} data-testid={`bp-${name}-input`} className={inputCls} value={s[name] || ""} onChange={field(name)} />
              </Field>
            ))}
            <Field label="Are there any social media accounts whose style you like?" hint="Up to five URLs.">
              <div className="space-y-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <input
                    key={i}
                    data-testid={`bp-inspiration-${i}-input`}
                    className={inputCls}
                    placeholder={`Inspiration account ${i + 1}`}
                    value={(s.inspiration || [])[i] || ""}
                    onChange={(e) => {
                      const list = [...(s.inspiration || ["", "", "", "", ""])];
                      list[i] = e.target.value;
                      setS({ inspiration: list });
                    }}
                  />
                ))}
              </div>
            </Field>
          </div>
        )}

        {/* STEP 6 CONTENT */}
        {key === "content" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">Content preferences</h2>
            <Field label="What type of content would you like? *" error={errors.content_types}>
              <CheckboxGroup name="bp-content-types" columns={3} options={CONTENT_TYPES} value={s.content_types || []} onChange={(v) => setS({ content_types: v })} />
            </Field>
            <Field label="What content should we avoid?" htmlFor="bp-avoid-content">
              <textarea id="bp-avoid-content" rows={3} data-testid="bp-avoid-content-input" className={inputCls} value={s.avoid_content || ""} onChange={field("avoid_content")} />
            </Field>
            <Field label="Do you want prices shown in posts?">
              <RadioGroupPills name="bp-prices" options={["Always", "Sometimes", "Never"]} value={s.show_prices || ""} onChange={(v) => setS({ show_prices: v })} />
            </Field>
            <Field label="Should posts include calls-to-action?">
              <RadioGroupPills name="bp-cta-usage" options={["Yes", "Sometimes", "No"]} value={s.cta_usage || ""} onChange={(v) => setS({ cta_usage: v })} />
            </Field>
            <Field label="Preferred calls-to-action">
              <CheckboxGroup name="bp-ctas" columns={3} options={CTA_OPTIONS} value={s.ctas || []} onChange={(v) => setS({ ctas: v })} />
            </Field>
          </div>
        )}

        {/* STEP 7 GOALS */}
        {key === "goals" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">What should your content achieve?</h2>
            <p className="text-sm text-slate-600">Select your goals, then use the arrows to rank them from highest to lowest priority.</p>
            <CheckboxGroup name="bp-goals" columns={3} options={GOALS} value={s.goals || []} onChange={(v) => setS({ goals: v })} />
            {(s.goals || []).length > 0 && (
              <div className="space-y-2" data-testid="goal-ranking">
                {(s.goals || []).map((g, i) => (
                  <div key={g} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5">
                    <span className="w-6 text-sm font-bold text-[#F95738]">{i + 1}</span>
                    <span className="flex-1 text-sm text-slate-800">{g}</span>
                    <Btn
                      variant="ghost"
                      testId={`goal-up-${i}`}
                      disabled={i === 0}
                      onClick={() => {
                        const list = [...s.goals];
                        [list[i - 1], list[i]] = [list[i], list[i - 1]];
                        setS({ goals: list });
                      }}
                    >
                      ↑
                    </Btn>
                    <Btn
                      variant="ghost"
                      testId={`goal-down-${i}`}
                      disabled={i === s.goals.length - 1}
                      onClick={() => {
                        const list = [...s.goals];
                        [list[i + 1], list[i]] = [list[i], list[i + 1]];
                        setS({ goals: list });
                      }}
                    >
                      ↓
                    </Btn>
                  </div>
                ))}
              </div>
            )}
            {[
              ["biggest_challenge", "What is your biggest social media challenge?"],
              ["success_definition", "What would make you feel PostMint is working well for your business?"],
              ["upcoming_goals", "Are there upcoming business goals we should know about?"],
            ].map(([name, label]) => (
              <Field key={name} label={label} htmlFor={`bp-${name}`}>
                <textarea id={`bp-${name}`} rows={3} data-testid={`bp-${name}-input`} className={inputCls} value={s[name] || ""} onChange={field(name)} />
              </Field>
            ))}
          </div>
        )}

        {/* STEP 8 PROMOTIONS */}
        {key === "promotions" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">Promotions &amp; important dates</h2>
            <p className="text-sm text-slate-600">
              Add promotions, events, product launches, holidays, closures, special opening hours, anniversaries or
              campaigns we should plan around.
            </p>

            {promotions.map((p, idx) => (
              <div key={idx} className="rounded-2xl border border-slate-200 p-4" data-testid={`profile-promotion-${idx}`}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Promotion Name">
                    <input data-testid={`profile-promotion-name-${idx}`} className={inputCls} value={p.name} onChange={(e) => {
                      const list = [...promotions]; list[idx] = { ...p, name: e.target.value }; setPromotions(list);
                    }} />
                  </Field>
                  <Field label="Call To Action">
                    <input className={inputCls} value={p.cta} onChange={(e) => {
                      const list = [...promotions]; list[idx] = { ...p, cta: e.target.value }; setPromotions(list);
                    }} />
                  </Field>
                </div>
                <Field label="Description">
                  <textarea rows={2} className={`mt-2 ${inputCls}`} value={p.description} onChange={(e) => {
                    const list = [...promotions]; list[idx] = { ...p, description: e.target.value }; setPromotions(list);
                  }} />
                </Field>
                <div className="mt-4 grid gap-4 sm:grid-cols-4">
                  {[["start_date", "Start Date", "date"], ["end_date", "End Date", "date"], ["price", "Price", "text"], ["discount", "Discount", "text"]].map(([n, l, t]) => (
                    <Field key={n} label={l}>
                      <input type={t} className={inputCls} value={p[n]} onChange={(e) => {
                        const list = [...promotions]; list[idx] = { ...p, [n]: e.target.value }; setPromotions(list);
                      }} />
                    </Field>
                  ))}
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {[["promo_code", "Promo Code"], ["url", "Website URL"]].map(([n, l]) => (
                    <Field key={n} label={l}>
                      <input className={inputCls} value={p[n]} onChange={(e) => {
                        const list = [...promotions]; list[idx] = { ...p, [n]: e.target.value }; setPromotions(list);
                      }} />
                    </Field>
                  ))}
                </div>
                <Field label="Additional Notes">
                  <textarea rows={2} className={`mt-2 ${inputCls}`} value={p.notes} onChange={(e) => {
                    const list = [...promotions]; list[idx] = { ...p, notes: e.target.value }; setPromotions(list);
                  }} />
                </Field>
                <Btn variant="danger" testId={`remove-promotion-${idx}`} className="mt-4" onClick={() => setPromotions(promotions.filter((_, i) => i !== idx))}>
                  Remove
                </Btn>
              </div>
            ))}

            <Btn variant="outline" testId="add-promotion-button" onClick={() => setPromotions([...promotions, { ...emptyPromotion }])}>
              Add Another Promotion
            </Btn>

            <Field label="Other important business dates" hint="Events, launches, holidays, closures, special opening hours, anniversaries, campaigns." htmlFor="bp-important-dates">
              <textarea id="bp-important-dates" rows={4} data-testid="bp-important-dates-input" className={inputCls} value={s.important_dates || ""} onChange={field("important_dates")} />
            </Field>
          </div>
        )}

        {/* STEP 9 UPLOADS */}
        {key === "uploads" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">Upload content</h2>
            <p className="text-sm text-slate-600">
              Use the Upload Media page for drag-and-drop uploads with progress, categories and per-file descriptions.
            </p>
            <div className="flex flex-wrap gap-2">
              {UPLOAD_CATEGORIES.map((c) => (
                <span key={c} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">{c}</span>
              ))}
            </div>
            <Link
              to="/dashboard/media"
              data-testid="profile-go-to-uploads"
              className="inline-flex rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white"
            >
              Go to Upload Media ({media.length} files)
            </Link>
            <Field label="Anything we should know about your uploads?" htmlFor="bp-upload-notes">
              <textarea id="bp-upload-notes" rows={3} data-testid="bp-upload-notes-input" className={inputCls} value={s.upload_notes || ""} onChange={field("upload_notes")} placeholder="New summer cocktail — Mojito Passion — €9.50" />
            </Field>
          </div>
        )}

        {/* STEP 10 PERMISSIONS */}
        {key === "permissions" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">Content permissions</h2>
            <Field label="Can PostMint use your uploaded images to create social media content? *" error={errors.can_use}>
              <RadioGroupPills name="bp-can-use" options={["Yes"]} value={s.can_use || ""} onChange={(v) => setS({ can_use: v })} />
            </Field>
            <Field label="Do you confirm that you own or have permission to use uploaded media? *" error={errors.owns_rights}>
              <RadioGroupPills name="bp-owns-rights" options={["Yes"]} value={s.owns_rights || ""} onChange={(v) => setS({ owns_rights: v })} />
            </Field>
            <Field label="Can uploaded media be modified, cropped or incorporated into graphics?">
              <RadioGroupPills name="bp-can-edit" options={["Yes", "No", "Ask before editing"]} value={s.can_edit || ""} onChange={(v) => setS({ can_edit: v })} />
            </Field>
            <Field label="Can people shown in uploaded images be included in social content?">
              <RadioGroupPills name="bp-people" options={["Yes", "No", "Depends on image"]} value={s.people_ok || ""} onChange={(v) => setS({ people_ok: v })} />
            </Field>
            <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
              You are responsible for ensuring you have the necessary rights and permissions for all uploaded material,
              including permission from any identifiable people shown in your photos or videos.
            </p>
          </div>
        )}

        {/* STEP 11 REVIEW */}
        {key === "review" && (
          <div className="space-y-5">
            <h2 className="font-display text-xl font-semibold text-slate-900">Review your profile</h2>
            <div className="space-y-3" data-testid="profile-review-summary">
              {[
                ["Business", `${data.business?.public_name || "—"} · ${data.business?.industry || "—"} · ${data.business?.city || "—"}`, 0],
                ["Audience", `${data.audience?.ideal_customer || "—"}`, 1],
                ["Tone", `${(data.brand?.tone_words || []).join(", ") || "—"} · ${data.brand?.language || "—"}`, 2],
                ["Visual Style", `${(data.visual?.visual_styles || []).join(", ") || "—"}`, 3],
                ["Platforms", `${(data.social?.platforms || []).join(", ") || "—"}`, 4],
                ["Content", `${(data.content?.content_types || []).slice(0, 5).join(", ") || "—"}`, 5],
                ["Goals", `${(data.goals?.goals || []).slice(0, 5).join(", ") || "—"}`, 6],
                ["Promotions", `${(data.promotions?.items || []).length} promotion(s) added`, 7],
                ["Uploaded Content", `${media.length} file(s)`, 8],
              ].map(([label, value, idx]) => (
                <div key={label} className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-200 p-4">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
                    <p className="mt-1 text-sm text-slate-800">{value}</p>
                  </div>
                  <Btn variant="outline" testId={`review-edit-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setStep(idx)}>
                    Edit
                  </Btn>
                </div>
              ))}
            </div>

            <label htmlFor="bp-confirm" className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 text-sm text-slate-700">
              <input
                id="bp-confirm"
                type="checkbox"
                data-testid="profile-confirm-checkbox"
                className="mt-1 h-4 w-4 accent-[#F95738]"
                checked={!!s.confirm_accurate}
                onChange={(e) => setS({ confirm_accurate: e.target.checked })}
              />
              I confirm the information provided is accurate.
            </label>
            {errors.confirm_accurate && <p className="text-sm text-red-600">{errors.confirm_accurate}</p>}

            <Btn testId="complete-profile-button" onClick={complete}>
              {completed ? "Update My Brand Profile" : "Complete My Brand Profile"}
            </Btn>
          </div>
        )}
      </Card>

      <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-md lg:mx-0 lg:rounded-2xl lg:border">
        <div className="flex flex-wrap items-center gap-2">
          <Btn variant="outline" testId="profile-previous-button" onClick={back} disabled={step === 0}>
            <ChevronLeft className="h-4 w-4" /> Previous
          </Btn>
          {step < STEPS.length - 1 && (
            <Btn testId="profile-continue-button" onClick={next} disabled={saving}>
              Continue <ChevronRight className="h-4 w-4" />
            </Btn>
          )}
          <Btn variant="ghost" testId="profile-save-exit-button" onClick={saveExit} disabled={saving}>
            <Save className="h-4 w-4" /> Save &amp; Exit
          </Btn>
        </div>
      </div>
    </div>
  );
}
